import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Card,
  Row,
  Col,
} from "antd";
import {
  DeleteOutlined,
  LinkOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import PageTitle from "../PageTitle";
import { useParams } from "react-router-dom";
import useTenantNavigate from "../../hooks/useTenantNavigate";
import { useAxiosGet } from "../../hooks/useAxios";
import { toast } from "react-toastify";
import { postData } from "../../api/apiService";
import axios from "axios";
import FileUploadModal from "../FileUploadModal";
import { getFileUrl } from "../../utils/constants";
import { DOCUMENTS } from "../../api/endpointMap";
import { getEmbedToken } from "../../api/api";

const { Option } = Select;

/**
 * Manual "Add GST Additional Notice" form.
 *
 * Deliberately mirrors AddGSTNoticeForm (same client fetch, same file-upload
 * modal + handler, same layout) so the two manual-add screens stay consistent.
 * Only three things differ, and they are exactly what makes this the ADDITIONAL
 * notice form rather than the plain notice form:
 *   1. Field set — an Additional Notice carries an `arn` and no
 *      `amount` / `issued_by` (see the GSTAdditionalNotice model).
 *   2. Save doctype — "GST Additional Notice And Order" (routes to
 *      GSTAdditionalNotice in the backend's document-save registry).
 *   3. Redirect — back to the GST notices page with its "Additional Notices"
 *      tab pre-selected (`?activeTab=additional`), which is where a manually
 *      added additional notice actually shows up. (The separate
 *      /gstin/additional-notices/:clientId list is a legacy screen and is NOT
 *      where these records surface.)
 *
 * The backend already saves this doctype through DOCUMENTS.SAVE (it auto-sets
 * organization_id from the logged-in user and reads client_id from the
 * payload), so no backend change is needed — this form just posts to it.
 */
const AddAdditionalNoticeForm = ({ serviceType }) => {
  const [form] = Form.useForm();
  const { clientId } = useParams();
  const navigate = useTenantNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  // 30 Jul 2026 — the form now attaches three file kinds (notice /
  // order / reply). `selectedUploadKind` remembers which field the
  // shared upload modal is populating when the user picks a file.
  const [selectedUploadKind, setSelectedUploadKind] =
    useState("notice_letter");
  const [noticeLetter, setNoticeLetter] = useState(null);
  const [orderLetter, setOrderLetter] = useState(null);
  const [replyLetter, setReplyLetter] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const [fetchClientDetails] = useAxiosGet();

  const onFinish = async (values) => {
    const payload = {
      ...values,
      // GSTAdditionalNotice columns are client_id (FK) + client_name (label).
      // The form's `client` field is a readonly display of client_name; it is
      // not a model column, so strip it before sending.
      client_id: clientDetails?.id,
      client_name: clientDetails?.client_name,
      issue_date: values.issue_date
        ? values.issue_date.format("YYYY-MM-DD")
        : null,
      due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
    };
    delete payload.client;

    try {
      setLoading(true);

      await postData(DOCUMENTS.SAVE, {
        doctype: "GST Additional Notice And Order",
        document_data: payload,
      });

      toast.info("Additional Notice Added Successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      setTimeout(() => {
        // The GST notices page reads `activeTab=additional` to open the
        // "Additional Notices" tab (see GstNoticesList activeTabParam), which is
        // where the record just saved appears.
        navigate(
          `/gstin/notices/${clientId}?disabledType=active&activeTab=additional`
        );
      }, 500);
    } catch (err) {
      toast.error(err, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleUpload = async (file) => {
    try {
      setUploadLoading(true);

      const payload = new FormData();
      payload.append("is_private", "0");
      payload.append("file", file, file.name);

      const token = getEmbedToken() || localStorage.getItem("authToken") || "";
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/api/v1/files/upload",
        payload,
        config
      );

      const fileURL = response?.data?.file_url;

      if (fileURL) {
        // Route the uploaded URL to whichever attach button the user
        // clicked (notice / order / reply). Setters mirror the form
        // field so submit picks it up cleanly.
        if (selectedUploadKind === "order_letter") {
          form.setFieldsValue({ order_letter: fileURL });
          setOrderLetter(fileURL);
        } else if (selectedUploadKind === "reply_letter") {
          form.setFieldsValue({ reply_letter: fileURL });
          setReplyLetter(fileURL);
        } else {
          form.setFieldsValue({ notice_letter: fileURL });
          setNoticeLetter(fileURL);
        }
      }

      toast.info("File uploaded successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      handleModalClose();
    } catch (error) {
      toast.error("Error uploading file", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    fetchClientDetails({
      customURL: `/api/v1/clients/gst/${clientId}`,
      cb: (data) => {
        // GET /api/v1/clients/gst/{id} returns the client row flat (no
        // `result` wrapper) — same shape AddGSTNoticeForm relies on.
        setClientDetails(data || {});
      },
    });
  }, []);

  useEffect(() => {
    if (clientDetails) {
      form.setFieldValue("client", clientDetails?.client_name);
    }
  }, [clientDetails]);

  return (
    <div>
      <PageTitle title="New GST Additional Notice" />
      <div className="min-h-screen flex flex-col items-center justify-center bg-blue-100 p-4">
        <div className="w-full max-w-5xl bg-white shadow-lg rounded-2xl p-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="w-full"
          >
            <Card
              title="Additional Notice Details"
              bordered={false}
              style={{ border: "4px solid #f5f5f5", marginBottom: "16px" }}
              headStyle={{ borderBottom: "3px solid #f5f5f5" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Client" name="client">
                    <Input readOnly />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Ref ID" name="ref_id">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="ARN" name="arn">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Type" name="type">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Description" name="description">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Notice Status" name="notice_status">
                    <Select style={{ height: "42px" }}>
                      <Option value="Open">Open</Option>
                      <Option value="Close">Close</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Date of Issuance" name="issue_date">
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Due Date"
                    name="due_date"
                    rules={[
                      { required: true, message: "Please select a due date" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>

                {/* Notice / Order / Reply attach columns — three
                    single-file fields that map 1:1 to the new
                    GSTAdditionalNotice columns (notice_letter,
                    order_letter, reply_letter). All three live on
                    the row for the detail viewer to render as
                    file-link boxes. `attachKind` drives which state
                    setter + field the shared upload modal writes
                    to; the render helper below stamps out each row
                    from a small config array. */}
                {[
                  {
                    label: "Notice",
                    fieldName: "notice_letter",
                    value: noticeLetter,
                    setValue: setNoticeLetter,
                    kind: "notice_letter",
                  },
                  {
                    label: "Order",
                    fieldName: "order_letter",
                    value: orderLetter,
                    setValue: setOrderLetter,
                    kind: "order_letter",
                  },
                  {
                    label: "Reply",
                    fieldName: "reply_letter",
                    value: replyLetter,
                    setValue: setReplyLetter,
                    kind: "reply_letter",
                  },
                ].map((f) => (
                  <Col xs={24} sm={12} key={f.fieldName}>
                    <Form.Item label={f.label} name={f.fieldName}>
                      {f.value ? (
                        <div className="flex items-center space-x-2">
                          <a
                            href={getFileUrl(f.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button icon={<LinkOutlined />}>
                              <span>
                                {f.value?.split("/")?.pop()?.split("?")[0]}
                              </span>
                            </Button>
                          </a>

                          <Button
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              f.setValue(null);
                              form.setFieldsValue({ [f.fieldName]: "" });
                            }}
                          />
                        </div>
                      ) : (
                        <Button
                          onClick={() => {
                            setSelectedUploadKind(f.kind);
                            setIsModalVisible(true);
                          }}
                          icon={<UploadOutlined />}
                        >
                          Attach
                        </Button>
                      )}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            <div className="flex justify-end mt-4">
              <Button loading={loading} type="primary" htmlType="submit">
                {loading ? "Adding.." : "Add Additional Notice"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <FileUploadModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onUpload={handleUpload}
        disabled={uploadLoading}
      />
    </div>
  );
};

export default AddAdditionalNoticeForm;
